import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Settings,
  Save,
  Building2,
  MessageSquare,
  Users,
  Hash,
  Facebook,
  Instagram,
  Linkedin,
  Check,
  AlertCircle,
  ExternalLink,
  Key,
  RefreshCw,
  CheckCircle,
  XCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

const NetworkIcon = ({ network, className }) => {
  const icons = {
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin
  };
  const Icon = icons[network];
  return Icon ? <Icon className={className} /> : null;
};

export default function SettingsPage() {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState("");
  const [newHashtags, setNewHashtags] = useState({
    facebook: "",
    instagram: "",
    linkedin: ""
  });
  
  // Instagram connection state
  const [instagramStatus, setInstagramStatus] = useState(null);
  const [checkingInstagram, setCheckingInstagram] = useState(false);
  const [showInstagramDialog, setShowInstagramDialog] = useState(false);
  const [instagramCredentials, setInstagramCredentials] = useState({
    access_token: "",
    instagram_account_id: ""
  });
  const [savingInstagram, setSavingInstagram] = useState(false);
  
  // Facebook connection state
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [checkingFacebook, setCheckingFacebook] = useState(false);

  useEffect(() => {
    fetchBrand();
    checkInstagramStatus();
    checkFacebookStatus();
  }, []);

  const fetchBrand = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      if (response.data.length > 0) {
        setBrand(response.data[0]);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };
  
  const checkInstagramStatus = async () => {
    setCheckingInstagram(true);
    try {
      const response = await axios.get(`${API}/instagram/status`);
      setInstagramStatus(response.data);
    } catch (error) {
      setInstagramStatus({ configured: false, error: "Impossible de vérifier le statut" });
    } finally {
      setCheckingInstagram(false);
    }
  };
  
  const checkFacebookStatus = async () => {
    setCheckingFacebook(true);
    try {
      const response = await axios.get(`${API}/facebook/status`);
      setFacebookStatus(response.data);
    } catch (error) {
      setFacebookStatus({ configured: false, error: "Impossible de vérifier le statut" });
    } finally {
      setCheckingFacebook(false);
    }
  };
  
  const handleSaveInstagramCredentials = async () => {
    if (!instagramCredentials.access_token || !instagramCredentials.instagram_account_id) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    setSavingInstagram(true);
    try {
      const response = await axios.post(`${API}/instagram/connect`, instagramCredentials);
      toast.success(response.data.message);
      setShowInstagramDialog(false);
      setInstagramCredentials({ access_token: "", instagram_account_id: "" });
      // Refresh status
      await checkInstagramStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingInstagram(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/brands/${brand.id}`, {
        name: brand.name,
        tone: brand.tone,
        target_audience: brand.target_audience,
        keywords: brand.keywords,
        hashtags: brand.hashtags,
        networks: brand.networks
      });
      toast.success("Paramètres enregistrés avec succès");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !brand.keywords.includes(newKeyword.trim())) {
      setBrand({
        ...brand,
        keywords: [...brand.keywords, newKeyword.trim()]
      });
      setNewKeyword("");
    }
  };

  const removeKeyword = (keyword) => {
    setBrand({
      ...brand,
      keywords: brand.keywords.filter(k => k !== keyword)
    });
  };

  const addHashtag = (network) => {
    const hashtag = newHashtags[network].trim();
    if (hashtag && !brand.hashtags[network].includes(hashtag)) {
      setBrand({
        ...brand,
        hashtags: {
          ...brand.hashtags,
          [network]: [...brand.hashtags[network], hashtag.startsWith('#') ? hashtag : `#${hashtag}`]
        }
      });
      setNewHashtags({ ...newHashtags, [network]: "" });
    }
  };

  const removeHashtag = (network, hashtag) => {
    setBrand({
      ...brand,
      hashtags: {
        ...brand.hashtags,
        [network]: brand.hashtags[network].filter(h => h !== hashtag)
      }
    });
  };

  const toggleNetwork = (network) => {
    const networks = brand.networks.includes(network)
      ? brand.networks.filter(n => n !== network)
      : [...brand.networks, network];
    setBrand({ ...brand, networks });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="ai-thinking">
          <span>Chargement des paramètres...</span>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p>Aucune marque configurée</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Profil de marque
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configurez votre identité éditoriale pour personnaliser la génération de contenu
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="btn-primary"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>Enregistrement...</>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>

      {/* Brand Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            Informations de la marque
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom de la marque</Label>
            <Input
              id="name"
              value={brand.name}
              onChange={(e) => setBrand({ ...brand, name: e.target.value })}
              data-testid="brand-name-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Editorial Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" />
            Profil éditorial
          </CardTitle>
          <CardDescription>
            Ces paramètres guident l'IA dans la création de contenu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tone">Ton et style de communication</Label>
            <Textarea
              id="tone"
              value={brand.tone}
              onChange={(e) => setBrand({ ...brand, tone: e.target.value })}
              placeholder="Ex: Professionnel mais accessible, inspirant, avec une touche d'humour"
              rows={2}
              data-testid="brand-tone-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="audience">Audience cible</Label>
            <Textarea
              id="audience"
              value={brand.target_audience}
              onChange={(e) => setBrand({ ...brand, target_audience: e.target.value })}
              placeholder="Ex: Entrepreneurs solo, freelances, professionnels indépendants"
              rows={2}
              data-testid="brand-audience-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Keywords */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Mots-clés de la marque
          </CardTitle>
          <CardDescription>
            Mots-clés à intégrer naturellement dans le contenu
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {brand.keywords.map((keyword) => (
              <Badge
                key={keyword}
                variant="secondary"
                className="cursor-pointer hover:bg-destructive/20"
                onClick={() => removeKeyword(keyword)}
              >
                {keyword}
                <span className="ml-1 text-muted-foreground">×</span>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Ajouter un mot-clé"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
              data-testid="new-keyword-input"
            />
            <Button variant="outline" onClick={addKeyword} data-testid="add-keyword-btn">
              Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Networks & Hashtags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Hash className="w-4 h-4 text-primary" />
            Réseaux sociaux et hashtags
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {["facebook", "instagram", "linkedin"].map((network) => (
            <div key={network} className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id={`network-${network}`}
                  checked={brand.networks.includes(network)}
                  onCheckedChange={() => toggleNetwork(network)}
                  data-testid={`settings-network-${network}`}
                />
                <Label
                  htmlFor={`network-${network}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <NetworkIcon network={network} className="w-4 h-4" />
                  <span className="capitalize">{network}</span>
                </Label>
              </div>
              
              {brand.networks.includes(network) && (
                <div className="ml-7 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {brand.hashtags[network].map((hashtag) => (
                      <Badge
                        key={hashtag}
                        variant="outline"
                        className="cursor-pointer hover:bg-destructive/20"
                        onClick={() => removeHashtag(network, hashtag)}
                      >
                        {hashtag}
                        <span className="ml-1 text-muted-foreground">×</span>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ajouter un hashtag"
                      value={newHashtags[network]}
                      onChange={(e) => setNewHashtags({ ...newHashtags, [network]: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && addHashtag(network)}
                      className="max-w-xs"
                      data-testid={`hashtag-input-${network}`}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => addHashtag(network)}
                      data-testid={`add-hashtag-${network}`}
                    >
                      Ajouter
                    </Button>
                  </div>
                </div>
              )}
              
              {network !== "linkedin" && <Separator className="mt-4" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* API Status & Instagram Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" />
            Connexions aux réseaux sociaux
          </CardTitle>
          <CardDescription>
            Connectez vos comptes pour publier automatiquement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Instagram - Real Integration */}
            <div className="p-4 rounded-lg border border-border bg-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">Instagram</p>
                    <p className="text-sm text-muted-foreground">
                      {instagramStatus?.connected 
                        ? `@${instagramStatus.account?.username}` 
                        : instagramStatus?.configured 
                          ? "Token configuré" 
                          : "Non configuré"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {checkingInstagram ? (
                    <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : instagramStatus?.connected ? (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Connecté
                    </Badge>
                  ) : instagramStatus?.configured ? (
                    <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      À vérifier
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-muted text-muted-foreground">
                      <XCircle className="w-3 h-3 mr-1" />
                      Non connecté
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowInstagramDialog(true)}
                  data-testid="configure-instagram-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Configurer
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={checkInstagramStatus}
                  disabled={checkingInstagram}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${checkingInstagram ? 'animate-spin' : ''}`} />
                  Tester
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open('https://developers.facebook.com/apps', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Meta Developer
                </Button>
              </div>
            </div>
            
            {/* Facebook - Real Integration */}
            <div className="p-4 rounded-lg border border-border bg-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
                    <Facebook className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">Facebook</p>
                    <p className="text-sm text-muted-foreground">
                      {facebookStatus?.connected 
                        ? facebookStatus.page?.name || "Page connectée"
                        : facebookStatus?.configured 
                          ? "Token configuré" 
                          : "Non configuré"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {checkingFacebook ? (
                    <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : facebookStatus?.connected ? (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Connecté
                    </Badge>
                  ) : facebookStatus?.configured ? (
                    <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Token expiré
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-muted text-muted-foreground">
                      <XCircle className="w-3 h-3 mr-1" />
                      Non connecté
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Error message if token expired */}
              {facebookStatus?.error && facebookStatus.error.includes("expired") && (
                <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400 mb-3">
                  ⚠️ Le token Facebook a expiré. Veuillez en générer un nouveau dans Meta Developer.
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={checkFacebookStatus}
                  disabled={checkingFacebook}
                  data-testid="test-facebook-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${checkingFacebook ? 'animate-spin' : ''}`} />
                  Tester
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open('https://developers.facebook.com/tools/explorer/', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Renouveler Token
                </Button>
                {facebookStatus?.connected && facebookStatus.page?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`https://facebook.com/${facebookStatus.page.id}`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Voir la Page
                  </Button>
                )}
              </div>
            </div>
            
            {/* LinkedIn - Simulation */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                <span>LinkedIn</span>
              </div>
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                Mode simulation
              </Badge>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            💡 Facebook est connecté ! Instagram en attente de configuration. LinkedIn arrive bientôt.
          </p>
        </CardContent>
      </Card>
      
      {/* Instagram Configuration Dialog */}
      <Dialog open={showInstagramDialog} onOpenChange={setShowInstagramDialog}>
        <DialogContent className="max-w-lg" data-testid="instagram-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Instagram className="w-5 h-5" />
              Configurer Instagram
            </DialogTitle>
            <DialogDescription>
              Connectez votre compte Instagram Business pour publier automatiquement
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium mb-2">📋 Prérequis :</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Compte Instagram Business ou Creator</li>
                <li>Page Facebook liée au compte Instagram</li>
                <li>Application Meta Developer créée</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ig-token">Access Token</Label>
              <Input
                id="ig-token"
                type="password"
                placeholder="Votre token d'accès Instagram"
                value={instagramCredentials.access_token}
                onChange={(e) => setInstagramCredentials(prev => ({ ...prev, access_token: e.target.value }))}
                data-testid="ig-token-input"
              />
              <p className="text-xs text-muted-foreground">
                Obtenez-le via le Graph API Explorer
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ig-account-id">Instagram Business Account ID</Label>
              <Input
                id="ig-account-id"
                placeholder="Ex: 17841405793..."
                value={instagramCredentials.instagram_account_id}
                onChange={(e) => setInstagramCredentials(prev => ({ ...prev, instagram_account_id: e.target.value }))}
                data-testid="ig-account-id-input"
              />
              <p className="text-xs text-muted-foreground">
                Trouvé dans les paramètres de votre Page Facebook liée
              </p>
            </div>
            
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm">
              <p className="text-yellow-400 font-medium flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Important
              </p>
              <p className="text-muted-foreground mt-1">
                Après avoir sauvegardé, ajoutez ces valeurs à votre fichier <code className="bg-muted px-1 rounded">.env</code> backend et redémarrez le serveur.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstagramDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveInstagramCredentials}
              disabled={savingInstagram}
              className="btn-primary"
              data-testid="save-ig-credentials-btn"
            >
              {savingInstagram ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
